import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import LabelFormatCanvas from '../components/LabelFormatCanvas.jsx';

function Badge({ children, tone = 'default' }) {
  const toneClass =
    tone === 'highlight'
      ? 'bg-primary-container text-on-primary-container'
      : 'bg-surface-container-low text-on-surface-variant';

  return (
    <span className={`rounded-full px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.16em] ${toneClass}`}>
      {children}
    </span>
  );
}

export default function Models({ user }) {
  const [models, setModels] = useState([]);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [applyingId, setApplyingId] = useState('');

  const canApply = user?.role === 'ADMIN' || user?.role === 'DESIGNER';

  async function load() {
    setError('');
    try {
      const items = await api.listModels();
      setModels(items);
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function applyModel(item) {
    setApplyingId(item.id);
    setError('');
    setStatus('');
    try {
      const created = await api.applyModel(item.id, {});
      const templateName = created?.template?.name;
      setStatus(
        templateName
          ? `Modelo "${item.name}" aplicado. Etiqueta "${templateName}" criada no estúdio.`
          : `Modelo "${item.name}" aplicado. Formato criado: ${created?.name}.`,
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setApplyingId('');
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-on-surface-variant">Catálogo global</div>
          <h1 className="studio-uniform-title mt-1.5 font-h2 text-h2 tracking-[-0.04em] text-on-surface">Modelos</h1>
        </div>
        <div className="flex flex-wrap gap-2.5">
          <Link
            to="/formatos"
            className="rounded-[18px] border border-surface-container-high bg-surface px-3.5 py-2.5 text-[10px] font-semibold text-on-surface"
          >
            Ver formatos
          </Link>
          <Link
            to="/templates"
            className="rounded-[18px] bg-primary px-3.5 py-2.5 text-[10px] font-semibold text-on-primary shadow-[0_10px_20px_rgba(249,115,22,0.18)]"
          >
            Ir para etiquetas
          </Link>
        </div>
      </div>

      {error ? <div className="rounded-2xl border border-error/20 bg-error-container/40 px-4 py-3 text-[10px] text-error">{error}</div> : null}
      {status ? <div className="rounded-2xl border border-primary/10 bg-primary-container/40 px-4 py-3 text-[10px] text-on-primary-container">{status}</div> : null}

      {!canApply ? (
        <div className="rounded-[22px] border border-surface-container-high bg-surface px-4 py-4 text-[10px] leading-5 text-on-surface-variant">
          Seu perfil pode visualizar o catálogo, mas apenas administradores e designers podem aplicar modelos e gerar formatos.
        </div>
      ) : null}

      <div className="grid gap-3 xl:grid-cols-3">
        {models.length === 0 ? (
          <div className="rounded-[22px] border border-surface-container-high bg-surface px-4 py-4 text-[10px] text-on-surface-variant">
            Nenhum modelo global disponível ainda.
          </div>
        ) : (
          models.map((item) => (
            <article
              key={item.id}
              className="rounded-[24px] border border-surface-container-highest bg-surface px-5 py-5 shadow-[0_12px_24px_rgba(15,23,42,0.04)]"
            >
              <LabelFormatCanvas model={item} variant="thumbnail" className="mb-4" />

              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[11px] font-semibold text-on-surface">{item.name}</div>
                  <div className="mt-1 text-[10px] text-on-surface-variant">
                    {item.manufacturer || 'Fabricante não informado'}{item.model ? ` • ${item.model}` : ''}
                  </div>
                </div>
                <div className="flex gap-1.5">
                  {item.featured ? <Badge tone="highlight">Destaque</Badge> : null}
                  <Badge>{item.active ? 'Ativo' : 'Inativo'}</Badge>
                </div>
              </div>

              <p className="mt-3 text-[10px] leading-5 text-on-surface-variant">
                {item.description || 'Modelo global pronto para gerar um formato compatível no tenant.'}
              </p>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="rounded-[18px] bg-surface-container-low px-3 py-2.5">
                  <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Dimensío</div>
                  <div className="mt-1 text-[10px] font-semibold text-on-surface">
                    {item.widthMm} x {item.heightMm} mm
                  </div>
                </div>
                <div className="rounded-[18px] bg-surface-container-low px-3 py-2.5">
                  <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Colunas</div>
                  <div className="mt-1 text-[10px] font-semibold text-on-surface">{item.columns}</div>
                </div>
                <div className="rounded-[18px] bg-surface-container-low px-3 py-2.5">
                  <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Mídia</div>
                  <div className="mt-1 text-[10px] font-semibold text-on-surface">{item.mediaType}</div>
                </div>
                <div className="rounded-[18px] bg-surface-container-low px-3 py-2.5">
                  <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Estoque</div>
                  <div className="mt-1 text-[10px] font-semibold text-on-surface">
                    {item.stockType}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between gap-3">
                <div className="text-[10px] text-on-surface-variant">
                  Atualizado em {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(new Date(item.updatedAt))}
                </div>
                <button
                  type="button"
                  disabled={!canApply || applyingId === item.id}
                  onClick={() => applyModel(item)}
                  className="rounded-[18px] bg-primary px-3.5 py-2.5 text-[10px] font-semibold text-on-primary shadow-[0_10px_20px_rgba(249,115,22,0.18)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {applyingId === item.id ? 'Aplicando...' : 'Usar modelo'}
                </button>
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
}
